<template>
  <!-- Toolbar flotante arriba-centro:
       [candado] | separador | [pan, fit, +tools de diseño] | separador | [⋮ menú]
       El candado alterna navegación/edición. Reusa los id toolbar-* / emits existentes. -->
  <div class="epc-center-toolbar">
    <!-- Modo diseño (toggle primario, visible) + candado (navegación/edición) -->
    <div class="epc-control-group">
      <b-tooltip v-if="_Allowed('show_design_mode')" :label="$t('design_mode')" position="is-bottom">
        <div id="toolbar-designModeTool" class="epc-button inactive"
          data-action="design-mode"
          :class="{ active: designMode }"
          @click="$emit('epc-in-design-mode', !designMode)">
          <b-icon pack="fas" icon="pen-ruler"></b-icon>
        </div>
      </b-tooltip>

      <b-tooltip :label="navigationMode ? $t('navigation_mode') : $t('edit_mode')" position="is-bottom">
        <div id="toolbar-lockTool" class="epc-button inactive"
          :class="{ active: navigationMode }"
          @click="$emit('epc-toggle-navigation')">
          <b-icon pack="fas" :icon="navigationMode ? 'lock' : 'lock-open'"></b-icon>
        </div>
      </b-tooltip>
    </div>

    <!-- Navegación + herramientas de diseño -->
    <div class="epc-control-group">
      <!-- Selección (puntero): sale de las herramientas Vector/Texto y vuelve a selección. -->
      <b-tooltip v-if="designMode" :label="$t('selection')" position="is-bottom">
        <div id="toolbar-selectTool" class="epc-button inactive"
          :class="{ active: !isDrawing && !selectedTool }"
          @click="$emit('epc-select-tool')">
          <b-icon pack="fas" icon="arrow-pointer"></b-icon>
        </div>
      </b-tooltip>

      <b-tooltip :label="$t('move')" position="is-bottom">
        <div id="toolbar-panTool" class="epc-button inactive"
          :class="{ active: isDraggable }"
          @click="$emit('epc-click-pan')">
          <b-icon pack="fas" icon="hand-paper"></b-icon>
        </div>
      </b-tooltip>

      <b-tooltip :label="$t('fit_content')" position="is-bottom">
        <div id="toolbar-fitTools" class="epc-button inactive"
          @click="$emit('epc-click-fit')">
          <b-icon pack="fas" icon="arrows-to-circle"></b-icon>
        </div>
      </b-tooltip>

      <!-- Áreas: solo en modo diseño y vista structures (habilita Vector/Texto) -->
      <b-tooltip v-if="designMode && pictogramType === 'structures'" :label="$t('show_areas')" position="is-bottom">
        <div id="toolbar-areasTool" class="epc-button inactive"
          data-action="view-areas"
          :class="{ active: showAreas }"
          @click="$emit('epc-open-panel', 'view-areas')">
          <b-icon pack="fas" icon="draw-polygon"></b-icon>
        </div>
      </b-tooltip>

      <!-- Herramientas de diseño: solo en modo diseño -->
      <template v-if="designMode && pictogramType === 'structures' && showAreas">
        <b-tooltip :label="'Vector'" position="is-bottom">
          <div id="toolbar-vectorTool" class="epc-button inactive"
            :class="{ active: hasEditingBlock }"
            @click="$emit('epc-open-panel', 'panel-drawing')">
            <b-icon pack="fas" icon="pen-nib"></b-icon>
          </div>
        </b-tooltip>

        <b-tooltip :label="$t('text')" position="is-bottom">
          <div id="toolbar-textTool" class="epc-button inactive"
            :class="{ active: selectedTool === 'pencil' }"
            @click="$emit('epc-open-panel', 'tool-pencil')">
            <b-icon pack="fas" icon="font"></b-icon>
          </div>
        </b-tooltip>

        <!-- Eliminar el elemento (vector/texto) seleccionado. -->
        <b-tooltip :label="$t('delete')" position="is-bottom">
          <div id="toolbar-deleteTool" class="epc-button inactive"
            @click="$emit('epc-delete-selected')">
            <b-icon pack="fas" icon="trash"></b-icon>
          </div>
        </b-tooltip>
      </template>
    </div>

    <!-- Modo de estado que se pinta: progreso o incidencias (reubicado desde el chip
         flotante). El dato de ambos ya viene del endpoint; esto solo cambia qué se pinta.
         Solo en modo visualización (en diseño únicamente se ven controles de diseño). -->
    <div v-if="!designMode" class="epc-control-group">
      <b-tooltip :label="$t('progress')" position="is-bottom">
        <div class="epc-button inactive"
          :class="{ active: statusMode === 'progress' }"
          @click="$emit('epc-status-mode', 'progress')">
          <b-icon pack="fas" icon="chart-line"></b-icon>
        </div>
      </b-tooltip>

      <b-tooltip :label="$t('incidents')" position="is-bottom">
        <div class="epc-button inactive"
          :class="{ active: statusMode === 'incidents' }"
          @click="$emit('epc-status-mode', 'incidents')">
          <b-icon pack="fas" icon="triangle-exclamation"></b-icon>
        </div>
      </b-tooltip>
    </div>

    <!-- Menú ⋮ : acciones secundarias -->
    <div class="epc-control-group">
      <b-dropdown aria-role="list" position="is-bottom-left" :mobile-modal="false">
        <template #trigger>
          <div class="epc-button inactive" data-action="toolbar-more" role="button" :aria-label="$t('menu')">
            <b-icon pack="fas" icon="ellipsis-vertical"></b-icon>
          </div>
        </template>

        <b-dropdown-item v-if="designMode && _Allowed('show_insert')" aria-role="listitem" id="toolbar-insertTool"
          @click="$emit('epc-open-panel', 'panel-views')">
          <b-icon icon="solar-panel" pack="fas" size="is-small"></b-icon>
          <span class="epc-dd-label">{{ $t('pictograms') }}</span>
        </b-dropdown-item>

        <!-- LOD (overview por siluetas por % de avance). Solo en visualización; oculto en foco. -->
        <b-dropdown-item v-if="!isFocused && !designMode && _Allowed('show_lod')" aria-role="listitem"
          :class="{ 'epc-item-active': lodEnabled }"
          @click="$emit('epc-toggle-lod')">
          <b-icon :icon="lodEnabled ? 'square-check' : 'border-all'" pack="fas" size="is-small"></b-icon>
          <span class="epc-dd-label">LOD</span>
        </b-dropdown-item>

        <b-dropdown-item aria-role="listitem" id="toolbar-fullscreenTools"
          @click="$emit('epc-click-fullscreen')">
          <b-icon icon="expand" pack="fas" size="is-small"></b-icon>
          <span class="epc-dd-label">{{ $t('fullscreen') }}</span>
        </b-dropdown-item>
      </b-dropdown>
    </div>
  </div>
</template>

<script>
export default {
  name: "ViewerToolbarDesign",

  props: {
    designMode: { type: Boolean, default: false },
    pictogramType: { type: String, default: 'structures' },
    showAreas: { type: Boolean, default: false },
    selectedTool: { type: String, default: null },
    isDrawing: { type: Boolean, default: false },
    hasEditingBlock: { type: Boolean, default: false },
    isDraggable: { type: Boolean, default: false },
    navigationMode: { type: Boolean, default: false },
    fullscreenEnabled: { type: Boolean, default: false },
    statusMode: { type: String, default: 'progress' }, // 'progress' | 'incidents'
    lodEnabled: { type: Boolean, default: false },
    isFocused: { type: Boolean, default: false }, // en modo foco se oculta el LOD
    canActions: { type: Object, default() { return {} } }, // permisos por contexto
  },

  methods: {
    // Visible salvo que el contexto lo apague explícitamente (flag === false).
    _Allowed(action) {
      if (this.canActions[action] === undefined) return true
      return !!this.canActions[action]
    },
  },
}
</script>

<style scoped>
.epc-center-toolbar {
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: row;
  gap: 8px;
  pointer-events: none;
  z-index: 6;
}

.epc-control-group {
  background-color: #ffffff;
  border: 1px solid #e9e9ef;
  box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.10);
  color: #495057;
  border-radius: 10px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 2px;
  padding: 3px;
  pointer-events: all;
}

.epc-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 7px;
  cursor: pointer;
  color: #495057;
  position: relative;
  transition: background-color 0.12s ease, color 0.12s ease;
}

.epc-button:hover {
  background-color: #f2f2f7;
  color: #3a6bc8;
}

.epc-button.active {
  background-color: #e8eef8;
  color: #3a6bc8;
}

.epc-button:focus {
  outline: none;
}

/* Ítem del dropdown en estado activo (p.ej. LOD on). */
.epc-item-active {
  color: #3a6bc8;
  font-weight: 600;
}

/* Separación entre el icono y el texto en los ítems del dropdown ⋮. */
.epc-dd-label {
  margin-left: 8px;
}
</style>
